import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import BookingWidget from "@/components/BookingWidget";
import ReviewsSection from "@/components/ReviewsSection";
import Seo from "@/components/Seo";
import { orgSchema } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { CheckCircle, ShieldCheck, Airplane, ArrowRight, Star, Phone, WhatsappLogo, MapPin, Clock, EnvelopeSimple, Sparkle, Headset, CursorClick, CreditCard, Baby, ChatCircle, Receipt, SealCheck } from "@phosphor-icons/react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { BLOG_POSTS } from "@/data/blog";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, EMAIL, ADDRESS_LINE1, ADDRESS_LINE2, MAPS_URL } from "@/lib/contact";

const HERO = "/hero.webp";
const SVC_AIRPORT = "https://images.unsplash.com/photo-1715267174266-95519fabe0c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const SVC_CITY = "https://customer-assets.emergentagent.com/job_zurich-airport-cab/artifacts/dlcqutzp_IMG_1588.jpeg";
const SVC_HOURLY = "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/9b1d3336db82f663064974284769c797ef323d430f9d928f2cb083da8cc93c70.png";
const SVC_BUSINESS = "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/465d54e683935b7913c298c992c1590828c393372e5327b0d315bf57cd72728a.png";
const ECONOMY = "https://customer-assets.emergentagent.com/job_zurich-airport-cab/artifacts/x5ooji4t_IMG-20260704-WA0000.jpg";
const VAN = "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/0bf1e9dc45a1894080e939c366608a58879df43c993c7fc5440be65fc5e24a63.png";
const FIRST = "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/4a6f1bb5b20a7333c87fce7bbb7941d16e11e75aab59f90160f036b9295b132f.png";
const BIZ = "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/70c63dbf3a7a830cd4e30f3f5263d0e0192940397ace6a8a655c7bd970e8eedb.png";
const SPRINTER = "https://static.prod-images.emergentagent.com/jobs/d11c1f14-a29e-48e3-b4e1-8d772573ce5f/images/105ea2e1c70d04052a481ccadc7a6b88c282dc11a2160bb6240fd615917c0ae0.png";
const VCLASS = "https://images.pexels.com/photos/17455632/pexels-photo-17455632.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DEST = {
  zurich: "https://images.pexels.com/photos/15214029/pexels-photo-15214029.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  zermatt: "https://images.pexels.com/photos/28732871/pexels-photo-28732871.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  stmoritz: "https://images.unsplash.com/photo-1581196659434-70bf4293b04f?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  davos: "https://images.unsplash.com/photo-1676233361841-1ef03ab996ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  interlaken: "https://images.pexels.com/photos/37485919/pexels-photo-37485919.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lucerne: "https://images.pexels.com/photos/27561822/pexels-photo-27561822.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }) };
const rise = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } } };

const ContactCard = ({ icon: Icon, label, value, href }) => (
  <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="group rounded-3xl border border-[#E9E5DE] bg-white p-6 hover:border-[#EF5A1E] transition-colors duration-200">
    <div className="w-11 h-11 rounded-full bg-[#EEF1FF] flex items-center justify-center mb-4"><Icon size={20} weight="fill" className="text-[#EF5A1E]" /></div>
    <p className="font-body text-xs text-[#5A607A] uppercase tracking-wider">{label}</p>
    <p className="font-heading font-semibold text-[#0B1533] mt-1 break-words">{value}</p>
  </a>
);

export default function Home() {
  const { t, lang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [location]);

  const services = [
    { img: SVC_AIRPORT, title: t("svc_airport"), desc: t("svc_airport_desc"), slug: "zurich-airport-transfer-guide" },
    { img: SVC_CITY, title: t("svc_city"), desc: t("svc_city_desc"), slug: "zurich-to-davos-winter" },
    { img: SVC_HOURLY, title: t("svc_hourly"), desc: t("svc_hourly_desc"), slug: "business-travel-zurich" },
    { img: SVC_BUSINESS, title: t("svc_business"), desc: t("svc_business_desc"), slug: "fixed-fares-vs-taxi-meter" },
  ];
  const fleet = [
    { img: ECONOMY, label: "Economy Klasse", seats: 3, note: "Škoda Octavia o. ä.", slug: "economy-klasse" },
    { img: VAN, label: "Standard XXL Van", seats: 7, note: "Mercedes V-Klasse XXL", slug: "standard-xxl-van" },
    { img: FIRST, label: "First Class", seats: 3, note: "Mercedes-Maybach / S-Klasse", slug: "first-class" },
    { img: BIZ, label: "Business Class", seats: 3, note: "BMW 7er / Audi A8", slug: "business-class" },
    { img: SPRINTER, label: "Sprinter", seats: 14, note: "Mercedes Sprinter", slug: "sprinter" },
  ];

  return (
    <div data-testid="home-page">
      <Seo
        title={t("seo_home_title")}
        description={t("seo_home_desc")}
        path="/"
        jsonLd={orgSchema()}
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden bg-[#0B1533]">
        <div className="absolute inset-0">
          <img src={HERO} alt="Airport Transfer Zurich chauffeur beside a black Mercedes S-Class at Zürich Airport" fetchPriority="high" decoding="async" width="1920" height="1288" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1533]/75 via-[#0B1533]/80 to-[#0B1533]" />
        </div>
        <div className="relative z-10 w-full max-w-xl mx-auto px-4 sm:px-6 pt-28 pb-20 flex flex-col items-center">
          {/* Headline ABOVE the booking system */}
          <div className="w-full text-center mb-10 flex flex-col items-center gap-5">
            <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="font-body text-[11px] sm:text-xs uppercase tracking-[0.28em] text-[#EF5A1E]">
              {t("hero_kicker")}
            </motion.p>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1} className="font-heading font-bold tracking-tight text-4xl sm:text-5xl leading-[1.08] text-white max-w-2xl">
              {t("hero_title")}
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2} className="font-body text-base sm:text-lg text-white/70 max-w-lg leading-relaxed">
              {t("hero_sub")}
            </motion.p>
          </div>

          {/* Booking widget below the headline */}
          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="w-full">
            <BookingWidget />
          </motion.div>

          {/* WhatsApp + chips + trust stack BELOW the booking system */}
          <div className="w-full text-center mt-10 flex flex-col items-center gap-6">
            <motion.a variants={fade} initial="hidden" animate="show" custom={4} href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2.5 border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all duration-300 rounded-full py-3 px-8 font-body font-semibold">
              <WhatsappLogo size={20} weight="fill" /> {t("hero_whatsapp")}
            </motion.a>

            {/* Feature chips 2x2 */}
            <motion.div variants={fade} initial="hidden" animate="show" custom={5} className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg mt-2">
              {[t("hero_chip_1"), t("hero_chip_2"), t("hero_chip_3"), t("hero_chip_4")].map((c, i) => (
                <div key={i} data-testid={`hero-chip-${i}`} className="flex items-center gap-3 bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-xl px-4 py-3 text-left">
                  <CheckCircle size={20} weight="fill" className="text-[#25D366] shrink-0" />
                  <span className="font-body text-sm text-white font-medium">{c}</span>
                </div>
              ))}
            </motion.div>

            {/* Trust / rating card */}
            <motion.div variants={fade} initial="hidden" animate="show" custom={6} data-testid="hero-trust-card" className="flex items-center gap-4 bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 w-full max-w-lg">
              <div className="flex gap-1 shrink-0">
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={18} weight="fill" className="text-[#25D366]" />)}
              </div>
              <div className="text-left">
                <p className="font-heading font-semibold text-white text-sm">{t("hero_trust_title")}</p>
                <p className="font-body text-xs text-white/60">{t("hero_trust_sub")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ADVANTAGES BAND */}
      <section className="bg-[#F5F2ED] border-b border-[#E9E5DE]" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Sparkle, t: t("adv_personalised_t"), d: t("adv_personalised_d") },
              { icon: ShieldCheck, t: t("adv_vetted_t"), d: t("adv_vetted_d") },
              { icon: Headset, t: t("adv_service_t"), d: t("adv_service_d") },
              { icon: CursorClick, t: t("adv_easy_t"), d: t("adv_easy_d") },
            ].map((a, i) => (
              <motion.div key={i} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex flex-col gap-4" data-testid={`advantage-${i}`}>
                <div className="w-14 h-14 rounded-2xl bg-white border border-[#E9E5DE] flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <a.icon size={26} weight="regular" className="text-[#EF5A1E]" />
                </div>
                <h3 className="font-heading font-semibold text-lg tracking-tight text-[#0B1533]">{a.t}</h3>
                <p className="font-body text-sm text-[#5A607A] leading-relaxed">{a.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO BOOK */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32" data-testid="how-to-book-section">
        <div className="max-w-2xl mb-14">
          <p className="font-body text-sm text-[#EF5A1E] font-semibold uppercase tracking-[0.2em] mb-3">{t("htb_kicker")}</p>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B1533] leading-[1.05]">{t("htb_title")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            [t("htb_1_t"), t("htb_1_d")],
            [t("htb_2_t"), t("htb_2_d")],
            [t("htb_3_t"), t("htb_3_d")],
            [t("htb_4_t"), t("htb_4_d")],
            [t("htb_5_t"), t("htb_5_d")],
            [t("htb_6_t"), t("htb_6_d")],
          ].map((s, i) => (
            <motion.div key={i} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="relative rounded-3xl border border-[#E9E5DE] bg-white p-7 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-shadow duration-300" data-testid={`htb-step-${i}`}>
              <span className="font-heading font-bold text-4xl text-[#EF5A1E]/20">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-heading font-semibold text-xl tracking-tight text-[#0B1533] mt-3">{s[0]}</h3>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{s[1]}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES / ARRIVE AT YOUR BEST */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl mb-14">
          <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">{t("arrive_kicker")}</p>
          <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05] text-[#0B1533]">{t("arrive_title")}</h2>
          <p className="font-body text-[#5A607A] mt-5 text-lg">{t("arrive_sub")}</p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <motion.div
              key={i}
              variants={rise}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="group relative rounded-3xl overflow-hidden h-80 cursor-pointer"
              data-testid={`service-${i}`}
            >
              <Link to={`/blog/${s.slug}`} data-testid={`service-link-${i}`} aria-label={s.title} className="absolute inset-0 z-10" />
              <img src={s.img} alt={s.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533]/90 via-[#0B1533]/20 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <h3 className="font-heading font-semibold text-xl text-white tracking-tight">{s.title}</h3>
                <p className="font-body text-sm text-white/80 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TRUST & RATINGS BANNER */}
      <section className="bg-[#0B1533]" data-testid="trust-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <div className="flex gap-1">{[0, 1, 2, 3, 4].map((s) => <Star key={s} size={22} weight="fill" className="text-[#EF5A1E]" />)}</div>
            <span className="font-heading font-bold text-3xl text-white">4.8<span className="text-white/40 text-xl">/5</span></span>
          </div>
          <div className="h-px w-16 sm:h-10 sm:w-px bg-white/15" />
          <p className="font-body text-white/80 text-lg">{t("trust_banner")}</p>
        </div>
      </section>

      {/* UNIQUE EXPERIENCE (split editorial) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32" data-testid="unique-experience">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="font-body text-sm text-[#EF5A1E] font-semibold uppercase tracking-[0.2em] mb-3">{t("ux_kicker")}</p>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B1533] leading-[1.05]">{t("ux_title")}</h2>
            <p className="font-body text-[#5A607A] mt-5 text-lg leading-relaxed">{t("ux_para")}</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {[t("ux_b1"), t("ux_b2"), t("ux_b3"), t("ux_b4")].map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <SealCheck size={22} weight="fill" className="text-[#EF5A1E] shrink-0" />
                  <span className="font-body text-[#0B1533]">{b}</span>
                </div>
              ))}
            </div>
          </div>
          <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative rounded-3xl overflow-hidden h-[420px] shadow-[0_20px_50px_rgba(11,21,51,0.15)]">
            <img src={VCLASS} alt={lang === "de" ? "Luxuriöser Innenraum eines Mercedes V-Klasse Transferfahrzeugs" : "Luxurious interior of a Mercedes V-Class transfer vehicle"} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* FLEET */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533]">{t("our_fleet")}</h2>
          <p className="font-body text-[#5A607A] max-w-sm">{t("fleet_sub")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {fleet.map((v, i) => (
            <motion.div key={i} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="group rounded-3xl overflow-hidden border border-[#E9E5DE] bg-white flex flex-col" data-testid={`fleet-${i}`}>
              <Link to={`/blog/${v.slug}`} data-testid={`fleet-link-${i}`} aria-label={v.label} className="flex flex-col h-full">
                <div className="h-60 overflow-hidden bg-[#0B1533]"><img src={v.img} alt={v.label} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" /></div>
                <div className="p-6 flex items-end justify-between">
                  <div>
                    <span className="font-heading font-semibold text-2xl tracking-tight text-[#0B1533]">{v.label}</span>
                    <p className="font-body text-sm text-[#5A607A] mt-1">{v.note}</p>
                  </div>
                  <span className="font-body text-sm text-[#5A607A] shrink-0">{v.seats} {t("capacity")}</span>
                </div>
                <div className="px-6 pb-6 mt-auto">
                  <span className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-[#EF5A1E]">{t("learn_more")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BENEFITS (accordion) */}
      <section className="bg-[#F5F2ED] border-y border-[#E9E5DE]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="font-body text-sm text-[#EF5A1E] font-semibold uppercase tracking-[0.2em] mb-3">{t("ben_kicker")}</p>
              <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B1533] leading-[1.05]">{t("ben_title")}</h2>
              <p className="font-body text-[#5A607A] mt-5 leading-relaxed">{t("ben_para")}</p>
            </div>
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible defaultValue="ben-0" className="w-full">
                {[
                  { icon: CreditCard, t: t("ben_1_t"), d: t("ben_1_d") },
                  { icon: Airplane, t: t("ben_2_t"), d: t("ben_2_d") },
                  { icon: Baby, t: t("ben_3_t"), d: t("ben_3_d") },
                  { icon: Clock, t: t("ben_4_t"), d: t("ben_4_d") },
                  { icon: ChatCircle, t: t("ben_5_t"), d: t("ben_5_d") },
                  { icon: ShieldCheck, t: t("ben_6_t"), d: t("ben_6_d") },
                  { icon: Receipt, t: t("ben_7_t"), d: t("ben_7_d") },
                ].map((b, i) => (
                  <AccordionItem key={i} value={`ben-${i}`} className="border-b border-[#E9E5DE]" data-testid={`benefit-${i}`}>
                    <AccordionTrigger className="hover:no-underline py-5">
                      <span className="flex items-center gap-4 text-left">
                        <b.icon size={24} weight="regular" className="text-[#EF5A1E] shrink-0" />
                        <span className="font-heading font-semibold text-lg text-[#0B1533]">{b.t}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-[#5A607A] text-base leading-relaxed pl-10">{b.d}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* DISCOVER DESTINATIONS (Zürich only) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32" data-testid="destinations-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="font-body text-sm text-[#EF5A1E] font-semibold uppercase tracking-[0.2em] mb-3">{t("pop_kicker")}</p>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B1533] leading-[1.05]">{t("pop_title")}</h2>
          </div>
          <Link to="/destinations" className="hidden sm:inline-flex items-center gap-2 font-body font-semibold text-[#EF5A1E] shrink-0">{t("all_destinations")} <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { slug: "zurich", img: DEST.zurich, name: t("dest_zurich_city") },
            { slug: "lucerne", img: DEST.lucerne, name: t("dest_lucerne") },
            { slug: "interlaken", img: DEST.interlaken, name: "Interlaken" },
            { slug: "davos", img: DEST.davos, name: "Davos" },
            { slug: "st-moritz", img: DEST.stmoritz, name: "St. Moritz" },
            { slug: "zermatt", img: DEST.zermatt, name: "Zermatt" },
          ].map((d, i) => (
            <motion.div key={d.slug} variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="group relative rounded-3xl overflow-hidden h-72" data-testid={`dest-tile-${d.slug}`}>
              <Link to={`/airport-transfer-${d.slug}`} aria-label={d.name} className="absolute inset-0 z-10" />
              <img src={d.img} alt={d.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533]/90 via-[#0B1533]/20 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="flex items-center gap-2 font-body text-xs text-white/70 uppercase tracking-wider mb-1"><Airplane size={13} weight="fill" className="text-[#EF5A1E]" /> ZRH</div>
                <h3 className="font-heading font-semibold text-2xl text-white tracking-tight">{d.name}</h3>
                <span className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-[#EF5A1E] mt-2">{t("view_fixed_fare")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" /></span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BLOG TEASER */}
      <section className="bg-[#F5F2ED] border-y border-[#E9E5DE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">{t("blog_kicker")}</p>
              <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05]">{t("blog_title")}</h2>
            </div>
            <Link to="/blog" className="hidden sm:inline-flex items-center gap-2 font-body font-semibold text-[#EF5A1E] shrink-0">{t("nav_blog")} <ArrowRight size={16} /></Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {BLOG_POSTS.slice(0, 3).map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`home-blog-${p.slug}`} className="group flex flex-col rounded-3xl overflow-hidden bg-white border border-[#E9E5DE]">
                <div className="h-44 overflow-hidden"><img src={p.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" /></div>
                <div className="p-6">
                  <span className="font-body text-xs font-semibold text-[#EF5A1E] bg-[#EEF1FF] rounded-full px-3 py-1">{p.category[lang] || p.category.en}</span>
                  <h3 className="font-heading font-semibold text-lg tracking-tight text-[#0B1533] leading-snug mt-3">{p.title[lang] || p.title.en}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <ReviewsSection />

      {/* CONTACT */}
      <section id="contact" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">{t("contact_kicker")}</p>
            <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05]">{t("contact_title")}</h2>
            <p className="font-body text-[#5A607A] mt-5 text-lg max-w-lg">{t("contact_sub")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={`tel:${PHONE_TEL}`} data-testid="contact-call" className="inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#D2450F] transition-colors duration-200"><Phone size={18} weight="fill" /> {t("call_now")}</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:opacity-90 transition-opacity duration-200"><WhatsappLogo size={18} weight="fill" /> {t("whatsapp")}</a>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <ContactCard icon={Phone} label={t("phone_label")} value={PHONE_DISPLAY} href={`tel:${PHONE_TEL}`} />
            <ContactCard icon={WhatsappLogo} label="WhatsApp" value={PHONE_DISPLAY} href={WHATSAPP_URL} />
            <ContactCard icon={EnvelopeSimple} label="E-Mail" value={EMAIL} href={`mailto:${EMAIL}`} />
            <ContactCard icon={MapPin} label={t("address_label")} value={`${ADDRESS_LINE1}, ${ADDRESS_LINE2}`} href={MAPS_URL} />
            <div className="sm:col-span-2 rounded-3xl bg-[#0B1533] p-6 flex items-center gap-3">
              <Clock size={22} weight="fill" className="text-[#EF5A1E]" />
              <div>
                <p className="font-body text-xs text-white/50 uppercase tracking-wider">{t("hours_label")}</p>
                <p className="font-heading font-semibold text-lg text-white">{t("hours_value")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B1533]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-white leading-tight">{t("hero_title")}</h2>
            <p className="font-body text-white/60 mt-4 text-lg max-w-md">{t("cta_sub")}</p>
          </div>
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="cta-book-btn"
            className="group inline-flex items-center gap-3 bg-[#EF5A1E] text-white font-body font-semibold text-base rounded-full px-9 py-4 hover:bg-[#D2450F] transition-all duration-200"
          >
            {t("hero_book_now")}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
}
